import {
  NDA_DOCUMENT_TYPE,
  COMMERCIAL_LEASE_DOCUMENT_TYPE,
  EQUIPMENT_LEASE_DOCUMENT_TYPE,
  LOAN_AGREEMENT_DOCUMENT_TYPE,
  SPEC_SOLUTION_TYPE_NAME,
} from '@dc-shared-web/hooks/api/document/constants'

// Cutoff date for chat agentic service (Jan 1, 2025)
// Crunches created on or after this date use the chat agentic service
export const SVC_CHAT_CUTOFF_DATE = new Date('2025-01-01T00:00:00Z')

export const DEFAULT_SUGGESTIONS = [
  'What are the notice requirements for changes and delays?',
  'What is considered a delay event under the contract for which I get extensions of time?',
  'Is there a contingency and how does it work?',
  'What is the standard for substantial completion?',
  'What are the payment procedures?',
  'What are the insurance requirements?',
  'How do the liquidated damages work?',
  'How often do I need to update the schedule?',
  "Who pays deductibles on the builder's risk insurance policy?",
  'Do I have to keep working if the Owner does not pay on time?',
  'When does the owner have to pay me?',
  'Do I get an extension of time for pandemics or covid?',
  'Do I get an increase in the contract amount for increases in material prices?',
]

export const CHEAT_SHEET_SUGGESTIONS = [
  'Can we reduce our retainage?',
  'How can I get more time added to the project schedule?',
  'Is there a contingency and how does it work?',
  'Provide an overview of what our liquidated damages are.',
  'Summarize how I give notice.',
  'How many weather days do I have? Explain how I need to claim those.',
  'Can I post this project to social media?',
  'Give an overview of the change order process.',
  'Is there a submittal schedule?',
  'Summarize any supplier requirements we need on this project.',
  'Do I have to hold any meetings?',
  'What do we need to start work?',
  "What's the markup for change orders?",
  'When do I get paid?',
  'Summarize payment terms into super short bullet points for the project team.',
]

export const NDA_SUGGESTIONS = [
  'What information is considered confidential?',
  'What are the obligations regarding the handling and sharing of this information?',
  'How long is the confidentiality period?',
  'Under what conditions can the agreement be terminated?',
  'Are there any costs associated with the enforcement or potential breach of the agreement?',
  'What mechanisms are in place for dispute resolution? Is arbitration or mediation required?',
  'What actions can you take if the other party breaches confidentiality?',
]

export const COMMERCIAL_LEASE_AGREEMENT_SUGGESTIONS = [
  'What are your obligations as a tenant?',
  'What rights do you have? Are there restrictions that could affect your business operations?',
  'What is the lease term and what penalties exist for early termination?',
  'Can the lease be renewed, and under what terms?',
  'What are the rent amounts, escalations, additional costs (like CAM fees, property taxes, utilities), and security deposit requirements?',
  'What are the steps for resolving disputes regarding the property or terms of the lease?',
  'Is there a mediation or arbitration clause?',
  'What happens if the landlord fails to provide services or maintain the property as agreed?',
]

export const GENERAL_DOCUMENT_SUGGESTIONS = [
  'Please identify and summarize the key points in this document.',
  'Who are the parties to this document and what are their primary obligations?',
  'What are the effective and expiration dates of this document?',
  'What are the manifest and latent risks involved in this document?',
  'Are there any unusual or one-sided clauses that could impose unexpected obligations?',
  'What are the financial terms? Are all charges, fees, and payment terms clearly outlined and reasonable?',
  'Are the terms and conditions clear and specific, or are there ambiguous elements that need clarification?',
  'Is there a clear termination clause, and what are the conditions under which the agreement can be terminated?',
]

export const LOAN_AGREEMENT_SUGGESTIONS = [
  'What are your repayment obligations?',
  'Are there any covenants or conditions that could trigger a default?',
  'Is there a fixed term, and what are the penalties or conditions for early repayment?',
  'What are the interest rates, fees (origination, late payment, processing), and is the interest fixed or variable?',
  'What happens in the event of a dispute about payments or terms?',
  'Are there specific provisions for resolution?',
  'What are your rights if the lender fails to disburse funds as agreed or breaches other terms of the agreement?',
]

export const EQUIPMENT_LEASE_AGREEMENT_SUGGESTIONS = [
  'What are your responsibilities for maintenance and insurance?',
  'What happens if the equipment fails or is inadequate?',
  'How long is the lease term?',
  'Are there options to extend, purchase, or return the equipment?',
  'What are the penalties for early termination?',
  'What are the lease payments, late fees, and costs for non-compliance with terms such as maintenance?',
  'How are disputes about the equipment condition or lease terms handled?',
  'What recourse do you have if the leased equipment does not meet the agreed specifications or if the lessor fails to meet their obligations?',
]

export const SPECIFICATIONS_REVIEW_SUGGESTIONS = [
  'What are the submittal requirements for cast-in-place concrete?',
  'Who are the approved manufacturers for lighting control devices?',
  'What information (or content) must be included in any RFI we submit?',
  'Do the specs say anything about reusing forms in concrete work?',
  'What are the attic stock requirements for ceramic tiles?',
  'Is there a limit on the amount of construction waste generated on this project?',
  'What are the field quality control requirements for section 237300?',
  'Is delegated design required for storefronts?',
]

export const DOCUMENT_TYPES_SUGGESTIONS = {
  [NDA_DOCUMENT_TYPE]: NDA_SUGGESTIONS,
  [COMMERCIAL_LEASE_DOCUMENT_TYPE]: COMMERCIAL_LEASE_AGREEMENT_SUGGESTIONS,
  [EQUIPMENT_LEASE_DOCUMENT_TYPE]: EQUIPMENT_LEASE_AGREEMENT_SUGGESTIONS,
  [LOAN_AGREEMENT_DOCUMENT_TYPE]: LOAN_AGREEMENT_SUGGESTIONS,
  [SPEC_SOLUTION_TYPE_NAME]: SPECIFICATIONS_REVIEW_SUGGESTIONS,
  Drawings: null,
  'Safety document': null,
  'Master Service Agreement': null,
  'Insurance Policy': null,
  'Consulting Agreement': null,
  Other: null,
}

export const ERROR_MESSAGES = {
  unavailable:
    'Chat is not available for this document. Crunch it again to enable Chat.',
  default:
    'A server side error occurred. This sometimes happens when our connection is interrupted.',
  busy: 'Our chat service is currently handling a high volume of inquiries. Please wait.',
}

// Enhanced Loading State Configuration
export const LOADING_CONFIG = {
  initialMessage: 'Working...',
  initialDuration: 2000, // 2 seconds
  rotationInterval: 5000, // 5 seconds
  transitionDuration: 300, // 300ms
}

// Main loading messages (randomly selected after initial phase)
export const MAIN_LOADING_MESSAGES = [
  'Reviewing document...',
  'Finalizing answer...',
] as const

export type LoadingMessageCycle = { mainMessage: string; subMessage: string }

export const LOADING_MESSAGE_CYLCES: LoadingMessageCycle[] = [
  {
    mainMessage: MAIN_LOADING_MESSAGES[0],
    subMessage: 'Reading your document',
  },
  {
    mainMessage: MAIN_LOADING_MESSAGES[0],
    subMessage: 'Reviewing relevant pages',
  },
  {
    mainMessage: MAIN_LOADING_MESSAGES[0],
    subMessage: 'Checking every section',
  },
  {
    mainMessage: MAIN_LOADING_MESSAGES[1],
    subMessage: 'Still working on it',
  },
  {
    mainMessage: MAIN_LOADING_MESSAGES[1],
    subMessage: 'Digging into details',
  },
  {
    mainMessage: MAIN_LOADING_MESSAGES[1],
    subMessage: 'Finding best answer',
  },
  {
    mainMessage: MAIN_LOADING_MESSAGES[1],
    subMessage: 'Almost there',
  },
]

// Type definitions for loading messages
export type MainLoadingMessage = (typeof MAIN_LOADING_MESSAGES)[number]
